// THE ARITHMETIC OF THE SELECTION LIST, AWAY FROM THE RENDERING.
//
// Owner, 2026-09-24: a paged list inside the Selection panel, the first
// 500 of N, per-row ticks and a status control.
//
// THERE IS ONE RULE IN HERE THAT CAN DO REAL DAMAGE, so it lives in a
// function of its own with its own tests: THE COUNT ON THE BUTTON AND
// THE SET THE BUTTON WRITES TO COME FROM THE SAME PLACE, AND THAT PLACE
// IS NEVER THE LENGTH OF THE PAGE ON SCREEN. A page of 500 inside a
// selection of 41,204 writes 41,204 - because an untouched list acts on
// the whole selection, which is today's rule and the one the owner
// kept. If those two ever came apart the button would name one set and
// write another, and this is a status write on real elements rather
// than a highlight.
export const PAGE_SIZE = 500;

// The sentence the owner asked for, stated plainly. Null when there is
// no window - a list showing everything must not claim to be a first
// slice of itself.
export function windowLine(shown, total) {
  const s = Number(shown) || 0;
  const t = Number(total) || 0;
  if (!t) return null;
  if (s >= t) return null;
  return `showing the first ${s.toLocaleString()} of ${t.toLocaleString()}`;
}

// WHAT THE BUTTON IS ABOUT TO WRITE. Ticked rows narrow it to the
// ticked ones; an untouched list is the whole selection, BY REFERENCE -
// the page holds 500 ids, not 41,204, so it names the selection and the
// server resolves it through the same query the rows came from.
export function writeTarget({ ticked = [], total = 0, selectionId = null, uniqueIds = null, filters = null, allFiltered = false } = {}) {
  const picked = (ticked || []).filter(Boolean);
  // OWNER, 2026-09-28: "the request was to filter fragments, and not to
  // highlight with filter, filter only filters the view, only 3D selection and
  // selection panels is what selects", and "make sure actions of status change
  // affect only the highlighted/selected elements".
  //
  // WHAT THIS REPLACES. A filter used to OUTRANK the ids: filters set and
  // nothing ticked meant "write the whole filtered set", so a person who
  // clicked three things saw 41,419 and a button that would have written all of
  // them. The count never lied - the button said 41,419 - but a confirmation
  // that names a number nobody chose is a receipt, not consent, and the derived
  // set was the DEFAULT while the deliberate one was the exception.
  //
  // THE ORDER NOW, most deliberate first: ticked rows, then the 3D SELECTION,
  // then a selection document - which only a deliberate act makes. A FILTER IS
  // NOT A WRITE SHAPE IN ANY COMBINATION: `allFiltered` is accepted so callers
  // need not change, and is deliberately IGNORED, so ONE place refuses rather
  // than every caller remembering to.
  if (picked.length) return { count: picked.length, body: filters ? { filters, uniqueIds: picked } : { uniqueIds: picked }, whole: false, kind: 'ticked' };
  const ids = (uniqueIds || []).filter(Boolean);
  if (ids.length) return { count: ids.length, body: { uniqueIds: ids }, whole: true, kind: 'selection' };
  if (selectionId) return { count: Number(total) || 0, body: { selectionId }, whole: true, kind: 'document' };
  // A filter and nothing else: offered, not selected. The button is refused.
  return { count: 0, body: null, whole: false, kind: 'none', needsPick: true };
}

// WHAT THE NUMBER IS, IN WORDS. The count alone is what let a filter pass for a
// selection: it was always a real number and what it counted was never said.
export function describeTarget(t, total = 0) {
  const n = (x) => (Number(x) || 0).toLocaleString();
  if (!t) return '';
  switch (t.kind) {
    case 'ticked': return `${n(t.count)} ticked in this list`;
    case 'selection': return `${n(t.count)} selected in the model`;
    case 'document': return `${n(t.count)} in this selection`;
    default: return `${n(total)} match this filter - none selected. Click elements in the model to act on them.`;
  }
}


// "Set 41,204 elements to Installed". The count is the caller's, so it
// cannot disagree with the one writeTarget returns.
export function buttonLabel(count, statusLabel) {
  const n = Number(count) || 0;
  return `Set ${n.toLocaleString()} element${n === 1 ? "" : "s"} to ${statusLabel}`;
}

// How many pages, given a total. Used for the pager and for nothing
// else - it never decides what is written.
export const pageCount = (total, size = PAGE_SIZE) => Math.max(1, Math.ceil((Number(total) || 0) / size));
